import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) {
      toast.error("Passwords don't match");
      return;
    }
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. You can now sign in.");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050505] p-6 text-white">
      <div className="aurora opacity-50" />
      <div className="grain" />
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="glass relative w-full max-w-md rounded-3xl p-10"
      >
        <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-xl brand-bg">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <h1 className="font-display text-3xl font-light tracking-tight">Choose a new password</h1>
        <p className="mt-2 text-sm text-white/55">
          Set a new password to finish resetting your account.
        </p>

        {done ? (
          <div className="mt-8 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm text-emerald-200" data-testid="reset-success">
            <CheckCircle2 className="mb-2 h-5 w-5" />
            All set. Redirecting to sign in…
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/50">New password</Label>
              <Input
                type="password" required minLength={8} value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="border-white/10 bg-black/40 text-white"
                data-testid="reset-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/50">Confirm password</Label>
              <Input
                type="password" required minLength={8} value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="border-white/10 bg-black/40 text-white"
                data-testid="reset-password-confirm"
              />
            </div>
            <Button type="submit" disabled={submitting || !token} className="h-11 w-full rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="reset-submit">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update password
            </Button>
            <p className="text-center text-xs text-white/45">
              <Link to="/login" className="hover:text-white">Back to sign in</Link>
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}
